import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  CircularProgress,
  Box,
  Typography,
  Link,
} from "@mui/material";
import ErrorCard from "./ErrorCard";
import { useMsal } from "@azure/msal-react";

const IssueTable = ({
  endpoint,
  columns,
  transformData,
  fetchOptions,
  jiraBaseUrl,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { accounts, instance } = useMsal();
  const user = accounts[0];

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const responseToken = await instance.acquireTokenSilent({
          account: user,
        });
        const accessToken = responseToken.accessToken;

        const response = await fetch(
          endpoint + "?token=" + accessToken,
          fetchOptions || {}
        );
        if (!response.ok) {
          throw new Error(
            `Błąd sieci: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        let finalData;
        if (typeof transformData === "function") {
          finalData = transformData(data);
          if (!Array.isArray(finalData)) {
            console.warn(
              "transformData powinno zwracać tablicę, otrzymano:",
              finalData
            );
            finalData = [];
          }
        } else if (Array.isArray(data)) {
          finalData = data;
        } else {
          console.warn("Oczekiwano tablicy z API, otrzymano:", data);
          finalData = [];
        }
        if (isMounted) {
          setRows(finalData);
          setPage(0);
        }
      } catch (err) {
        console.error(err);
        if (isMounted) {
          setError(err.message || "Nieznany błąd");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [endpoint, transformData, fetchOptions, instance, user]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // wybierz wiersze dla bieżącej strony
  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <TableContainer>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <ErrorCard error={error} />
        ) : rows.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography>Brak danych do wyświetlenia.</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.field} align={col.align || "left"}>
                    {col.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((row, rowIndex) => {
                const key = row.id != null ? row.id : rowIndex;
                return (
                  <TableRow key={key}>
                    {columns.map((col) => {
                      const value = row[col.field];
                      if (col.field === "id" && jiraBaseUrl && value != null) {
                        const href = jiraBaseUrl.endsWith("/")
                          ? `${jiraBaseUrl}${value}`
                          : `${jiraBaseUrl}/${value}`;
                        return (
                          <TableCell
                            key={col.field}
                            align={col.align || "left"}
                          >
                            <Link
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                            >
                              {value}
                            </Link>
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={col.field} align={col.align || "left"}>
                          {value != null ? String(value) : "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {!loading && !error && rows.length > 0 && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </>
  );
};

IssueTable.propTypes = {
  endpoint: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string.isRequired,
      align: PropTypes.oneOf(["left", "right", "center"]),
    })
  ).isRequired,
  transformData: PropTypes.func,
  fetchOptions: PropTypes.object,
  jiraBaseUrl: PropTypes.string,
};

export default IssueTable;
